// External libraries
import React, { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { useQuery } from "@tanstack/react-query"
import { Search, Close } from "@carbon/icons-react"

// Shadcn components
import { Input } from "../Shadcn/Input"
import { Typography } from "../Shadcn/Typography"
import { CollapsibleSection } from "../Shadcn/Collapsible"
import { RadioGroup, RadioCard } from "../Shadcn/RadioGroup"
import { TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent } from "../Shadcn/Tooltip"
import { Select } from "../Shadcn/Select"
import { Button } from "../Shadcn/Button"
import { topRightToast } from "../Shadcn/Sonner"


// Home components
import { BrandWatchList, PresenceConfig, POICategoryListHome, TopDistricts } from "."
import { OverlayMetricPopup } from "./OverlayMetricPopup"
import { AggLayerPopup } from "./AggLayerPopup"
import { LayerCheckboxRow } from "./sidebar/LayerCheckboxRow"
import { getMarkerColor } from "./markerUtils"
import { FilterRegionModal } from "../header"

// State
import {
	brandList, CIFType, cifTypeState, choroplethModeState, selectedCategoricalLayerState,
	dynamicPoiTogglesState, dynamicTogglesState, errorState,
	expandedPresenceConfigState, filtersState, geoState, hexLoadingState,
	LegendGeoLevel, markerFilterDataState, markerFiltersPayloadState, markerFilterSlidersState, metricState, overlayCountState, POICategories,
	poiMapColorState, prevCifTypeState, selectedBrands, selectedCategoryState,
	selectedTabState, selAggLayerState, selMarkerCategoriesState, selPOICategories, topFiveRegionsState,
} from "../../states"

// Services & helpers
import { useCIFService, useMapsService } from "../../services"
import {
	cleanFilterTree, getCurrGeoLevelName, getLayerInfoText,
	getParentGeoId, subGeoLevel,
} from "../../helpers"
import { BOUNDARY_TYPES, MetricKey, RouteConstants } from "../../constants"
import { GeoId } from "../../types/geo.types"

// ── Types ─────────────────────────────────────────────────────────────────────

interface LeftSideBarV2Props {
	isChecked: {
		MBP: boolean; spotlight: boolean; poimarker: boolean
		subregionBoundaries: boolean; adjacency: boolean; catchments: boolean
	}
	toggleSwitch: (e: any, name?: string, checked?: boolean) => void
	presenceTypeCount: any[]
	legendShimmer: boolean
	fetchMyPresence: (payload: any) => void
	setMbpFilter: React.Dispatch<React.SetStateAction<any>>
	setMapSpinner: React.Dispatch<React.SetStateAction<number>>
	mapSpinner: number
	myPresence: { info: any[]; markers: any[] }
	isPoiOverlapped: boolean
	geoCode: GeoId
	catchmentTypes: any
	isCatchmentAvailable: boolean
	selCatchmentType: any
	setSelCatchmentType: React.Dispatch<React.SetStateAction<any>>
	selSubRegion: any
	setSelSubRegion: React.Dispatch<React.SetStateAction<any>>
	catchment: any
	layersData: any[]
	selectedDataTab: string
	setSelectedDataTab: React.Dispatch<React.SetStateAction<string>>
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function isSameHierarchy(prevGeo: any, newGeo: any): boolean {
	const levels = ["country", "state", "district", "sub_district", "village"] as const
	for (const level of levels) {
		if (prevGeo[level] && newGeo[level] && prevGeo[level] !== newGeo[level]) return false
	}
	return true
}

const getSectionIcon = (iconUrl?: string) => {
	if (!iconUrl) return undefined
	return <img src={iconUrl} alt="" className="h-4 w-4 opacity-60" />
}

// ── Component ──────────────────────────────────────────────────────────────────

export const LeftSideBarV2: React.FC<LeftSideBarV2Props> = ({
	isChecked, toggleSwitch, presenceTypeCount, fetchMyPresence,
	legendShimmer, setMbpFilter, setMapSpinner, mapSpinner, myPresence,
	isPoiOverlapped, geoCode, catchmentTypes, isCatchmentAvailable,
	selCatchmentType, setSelCatchmentType, selSubRegion, setSelSubRegion,
	catchment, layersData, selectedDataTab, setSelectedDataTab,
}) => {
	const { pathname } = useLocation()
	const navigate = useNavigate()
	const mapService = useMapsService()
	const cifService = useCIFService()

	// ── Recoil ────────────────────────────────────────────────────────────────
	const setSelBrands = useSetRecoilState(selectedBrands)
	const setSelectedCategory = useSetRecoilState(selectedCategoryState)
	const poiCategories = useRecoilValue(POICategories)
	const poiColors = useRecoilValue(poiMapColorState)
	const topFiveRegions = useRecoilValue(topFiveRegionsState)
	const selectedGeo = useRecoilValue(geoState)
	const expandedPresenceConfig = useRecoilValue(expandedPresenceConfigState)
	const selectedTab = useRecoilValue(selectedTabState)
	const filters = useRecoilValue<any[]>(filtersState)
	const markerFiltersPayload = useRecoilValue<any[]>(markerFiltersPayloadState)
	const setMarkerFilterSliders = useSetRecoilState(markerFilterSlidersState)
	const [selMarkerCategories, setSelMarkerCategories] = useRecoilState(selMarkerCategoriesState)
	const markerFilterData = useRecoilValue(markerFilterDataState)
	const hexLoading = useRecoilValue(hexLoadingState)
	const [dynamicToggles, setDynamicToggles] = useRecoilState(dynamicTogglesState)
	const [dynamicPoiToggles, setDynamicPoiToggles] = useRecoilState(dynamicPoiTogglesState)
	const [brands, setBrands] = useRecoilState(brandList)
	const [selCategoryList, setSelCategoryList] = useRecoilState(selPOICategories)
	const [cifType, setCifType] = useRecoilState(cifTypeState)
	const [selMetric, setSelMetric] = useRecoilState(metricState)
	const [prev, setPrev] = useRecoilState(prevCifTypeState)
	const [selAggLayer, setSelAggLayer] = useRecoilState(selAggLayerState)
	const [overlayCount] = useRecoilState<any>(overlayCountState)
	const setError = useSetRecoilState(errorState)
	const [choroplethMode, setChoroplethMode] = useRecoilState(choroplethModeState)

	// ── Local state ───────────────────────────────────────────────────────────
	const [searchQuery, setSearchQuery] = useState("")
	const [isListOpen, setIsListOpen] = useState<{ categories: string | false; brands: string | false; aggLayer: boolean }>({ categories: false, brands: false, aggLayer: false })
	const [expandedTopDistricts, setExpandedTopDistricts] = useState(false)
	const [myBusinessPresenceConfig, setMyBusinessPresenceConfig] = useState<any>([])
	const [isOpenMetricPopup, setIsOpenMetricPopup] = useState({
		EH: false, CTV: false, PoI: false, catchments: false, subregionBoundaries: false,
	})
	const [toggleState, setToggleState] = useState<Record<string, boolean>>({
		MBP: !!isChecked?.MBP, spotlight: !!isChecked?.spotlight,
		poimarker: !!isChecked?.poimarker, adjacency: !!isChecked?.adjacency,
		catchments: !!isChecked?.catchments, EH: cifType.includes("EH"),
		CTV: cifType.includes("CTV"), PoI: cifType.includes("PoI"),
		subregionBoundaries: !!isChecked?.subregionBoundaries,
		...dynamicToggles, ...dynamicPoiToggles,
	})
	const [aggLayerPopupPos, setAggLayerPopupPos] = useState({ top: 0, left: 0 })
	const [popupPos, setPopupPos] = useState<{ left: any; bottom: any; top: any }>({ left: "0", bottom: "auto", top: "auto" })
	const [brandPopupPos, setBrandPopupPos] = useState<{ left: any; bottom: any; top: any }>({ left: "0", bottom: "auto", top: "auto" })

	const [showFilterRegionModal, setShowFilterRegionModal] = useState(false)
	const [sliders, setSliders] = useState<Record<string, any[]>>({})
	const [selectedCategoricalLayer, setSelectedCategoricalLayer] = useRecoilState(selectedCategoricalLayerState)

	// ── Refs ──────────────────────────────────────────────────────────────────
	const preCategoricalCifTypeRef = useRef<CIFType[]>([])
	const preCategoricalPrevRef = useRef<any[]>([])
	const preCategoricalDynamicTogglesRef = useRef<Record<string, boolean>>({})
	const prevGeoRef = useRef(selectedGeo)
	const prevBrandsTypeRef = useRef<string>("")
	const baseBrandsPayloadRef = useRef<any>(null)
	const dynamicPoiTogglesRef = useRef(dynamicPoiToggles)
	const prevChoroplethCountRef = useRef(0)
	const prevPoiMarkerCountRef = useRef(0)
	const refs = {
		button: useRef<HTMLDivElement>(null),
		brandButton: useRef<HTMLDivElement>(null),
		catCheckBox: useRef<HTMLDivElement>(null),
		brandCheckBox: useRef<HTMLDivElement>(null),
		aggLayerPopup: useRef<HTMLDivElement>(null),
		aggLayerBtn: useRef<HTMLSpanElement>(null),
	}
	const overlayRefs = {
		subregionBoundaries: useRef<HTMLDivElement>(null),
		EH: useRef<HTMLDivElement>(null),
		CTV: useRef<HTMLDivElement>(null),
		PoI: useRef<HTMLDivElement>(null),
		catchments: useRef<HTMLDivElement>(null),
	}

	// ── Derived / Memoised ────────────────────────────────────────────────────
	const [searchParams] = useSearchParams()
	const catchmentId = searchParams.get("catchment")!
	const totalOverlayCount = Object.values(overlayCount || {}).reduce(
		(acc: number, item) => acc + (item as number), 0
	)
	const isAggLayerOff = !selAggLayer
	const isHexAvailable = poiColors?.rootProperties?.isHexAvailable !== false
	const isRegionAvailable = poiColors?.rootProperties?.isRegionAvailable !== false

	const catchmentOption = { label: "Catchment", value: selSubRegion, key: "catchment" }
	const hexGridOption = {
		label: "Hex Grid", value: { key: "hexgrid", title: "Hex Grid" },
		key: "hexgrid", disabled: !isHexAvailable,
	}
	const subLevel = subGeoLevel(selectedGeo, selectedTab, catchment) as LegendGeoLevel

	/**
	 * Flat lookup map: mapping key → items array.
	 * Computed once per layersData change; used both in rendering and in disable logic.
	 */
	const layersMap = React.useMemo(() => {
		const map: Record<string, any[]> = {}
		layersData.forEach((group) => { map[group.mapping] = group.items || [] })
		return map
	}, [layersData])

	/** All choropleth items with sub-mapping === "numeric" (or absent) */
	const numericChoroplethItems = React.useMemo(
		() => (layersMap["choropleth"] || []).filter(
			(l: any) => l["sub-mapping"] === "numeric"
		),
		[layersMap]
	)


	/** All marker items */
	const allMarkerItems = layersMap["markers"] || []

	/** TopDistricts data-layer group */
	const dataLayersGroup = layersMap["layers"] || []

	// ── Toggle key resolver ───────────────────────────────────────────────────
	const getToggleKey = (layer: any) => {
		const known = ["EH", "CTV", "PoI", "MBP", "spotlight", "poimarker", "adjacency", "subregionBoundaries", "catchments"]
		if (known.includes(layer.type)) return layer.type
		if (layer?.markertype?.toLowerCase() === "poi") return layer.type
		return layer.layer_id || layer.type
	}

	const hasCategoricalLayers = React.useMemo(() => {
		const legend = poiColors?.legendProperties?.[subLevel]
		const hasData: string[] = Array.isArray(legend?.isDataAvailable) ? legend!.isDataAvailable : []
		const hasDataAvailabilityInfo = Array.isArray(legend?.isDataAvailable)
		return (layersMap["choropleth"] || []).some((l: any) => {
			const isCategorical = l["sub-mapping"] === "categorical" || (l?.status === "inprogress" && l["sub-mapping"] !== "numeric")
			if (!isCategorical) return false
			if (!hasDataAvailabilityInfo) return true
			if (l?.status === "inprogress") return true
			const k = getToggleKey(l)
			const id = l.name || l.type || k
			return hasData.includes(k) || hasData.includes(id)
		})
	}, [layersMap, poiColors, subLevel])

	useEffect(() => {
		if (choroplethMode === "categorical" && !hasCategoricalLayers) {
			topRightToast.info(
				"No categorical layers available",
				"Add a new categorical layer to view choropleth, or shift modes."
			)
		}
	}, [choroplethMode, hasCategoricalLayers])

	// ── Disable logic ─────────────────────────────────────────────────────────
	const isTypeDisable = (type: string) => {
		const legend = poiColors?.legendProperties?.[subLevel]
		const available: string[] = Array.isArray(legend?.isDataAvailable) ? (legend?.isDataAvailable ?? []) : []
		if (type === "CTV" && (selectedGeo.district || !available.includes("CTV"))) return true
		if (type === "EH" && !available.includes("EH")) return true
		if (type === "PoI" && !selectedGeo.sub_district && !selectedGeo.village && !available.includes("PoI")) return true
		if (type === "adjacency" && (geoCode === selectedGeo.country || catchment)) return true
		if (type === "subregionBoundaries" && pathname === RouteConstants.root &&
			(isChecked?.catchments || geoCode === selectedGeo.country || geoCode === selectedGeo.state ||
				geoCode === selectedGeo.district || String(geoCode) === String(selectedGeo.village))) return true
		if (type === "catchments" && (catchmentTypes?.length === 0 || !isCatchmentAvailable ||
			pathname === RouteConstants.catchments || String(geoCode) === String(selectedGeo.village))) return true
		return false
	}

	const isLayerToggled = (layer: any) => {
		const k = getToggleKey(layer)
		if (["EH", "CTV", "PoI"].includes(layer.type)) {
			if (choroplethMode === "categorical")
				return preCategoricalCifTypeRef.current.includes(layer.type as CIFType)
			return cifType.includes(layer.type as CIFType)
		}
		if (layer?.markertype?.toLowerCase() === "poi")
			return dynamicPoiToggles[k] !== undefined ? dynamicPoiToggles[k] : !!toggleState[k]
		if (choroplethMode === "categorical" && layer["sub-mapping"] === "numeric") {
			const saved = preCategoricalDynamicTogglesRef.current
			if (k in saved) return saved[k]
		}
		return !!toggleState[k]
	}

	const isLayerDisabled = (layer: any, isChoropleth = false) => {
		const k = getToggleKey(layer)
		const isChoroplethMetric = ["EH", "CTV", "PoI"].includes(layer.type)
		const isPoiMarker = layer?.markertype?.toLowerCase() === "poi"
		const isToggled = isLayerToggled(layer)

		// Choropleth 5-layer limit (numeric layers only)
		if (isChoropleth && !isPoiMarker) {
			const enabled = numericChoroplethItems.filter((l: any) => !!toggleState[getToggleKey(l)]).length
			if (enabled >= 5 && !isToggled) return true
		}

		// POI marker 5-layer limit
		if (isPoiMarker) {
			const enabled = allMarkerItems
				.filter((l: any) => l.markertype?.toLowerCase() === "poi")
				.filter((l: any) => {
					const mk = getToggleKey(l)
					return dynamicPoiToggles[mk] !== undefined ? dynamicPoiToggles[mk] : !!toggleState[mk]
				}).length
			if (enabled >= 5 && !isToggled) return true
		}

		// No data at this geo level (choropleth only)
		const legend = poiColors?.legendProperties?.[subLevel]
		const hasData: string[] = Array.isArray(legend?.isDataAvailable) ? (legend?.isDataAvailable ?? []) : []
		const id = layer.name || layer.type || k
		if (isChoropleth && !isPoiMarker && !hasData.includes(k) && !hasData.includes(id) && layer?.status !== "inprogress") return true

		if (isChoropleth && isAggLayerOff) return true
		if (layer.type === "MBP") return !layer.hasmbp
		return isTypeDisable(layer.type)
	}

	const getCustomTooltip = (layer: any, isChoropleth = false) => {
		const k = getToggleKey(layer)
		const isPoiMarker = layer?.markertype?.toLowerCase() === "poi"
		const isToggled = isLayerToggled(layer)
		if (layer?.status === "inprogress") return "Layer is being processed"
		if (isChoropleth && !isPoiMarker) {
			const enabled = numericChoroplethItems.filter((l: any) => !!toggleState[getToggleKey(l)]).length
			if (enabled >= 5 && !isToggled) return "You have reached the limit of 5 active choropleth layers"
		}
		if (isPoiMarker) {
			const enabled = allMarkerItems
				.filter((l: any) => l.markertype?.toLowerCase() === "poi")
				.filter((l: any) => {
					const mk = getToggleKey(l)
					return dynamicPoiToggles[mk] !== undefined ? dynamicPoiToggles[mk] : !!toggleState[mk]
				}).length
			if (enabled >= 5 && !isToggled) return "You have reached the limit of 5 active POI marker layers"
		}
		if (isChoropleth && isAggLayerOff) return "Turn on Choropleth to enable this toggle"
		const legend = poiColors?.legendProperties?.[subLevel]
		const hasData: string[] = Array.isArray(legend?.isDataAvailable) ? (legend?.isDataAvailable ?? []) : []
		const id = layer.name || layer.type || k
		if (isChoropleth && !isPoiMarker && !hasData.includes(k) && !hasData.includes(id))
			return `We do not have ${layer.name || layer.type} at this level`
		if (layer.type === "catchments" && isLayerDisabled(layer) && !isToggled)
			return !isCatchmentAvailable
				? "No catchment available in this area. Catchment has been turned off."
				: "We have not received your business catchment data to visualise"
		if (layer.type === "MBP") return "No business presence data available."
		return ""
	}

	// ── Toggle handler ────────────────────────────────────────────────────────
	const handleToggleChange = (checked: boolean, name: string, options?: any[], isChoropleth = false) => {
		if (name === "EH" || name === "CTV" || name === "PoI") {
			let updated: any = []
			setCifType((prev) => { updated = checked ? [...new Set([...prev, name])] : prev.filter((i) => i !== name); return updated })
			setPrev((prev) => {
				const n = checked ? [...new Set([...prev, name])] : prev.filter((i) => i !== name)
				localStorage.setItem("selectedtoggles", JSON.stringify(n)); return n
			})
			if (options?.length) {
				setSelMetric((prev) => {
					const upd = { ...prev }
					const opt = prev[name as MetricKey] || options[0]
					upd[name as MetricKey] = opt ? { ...opt, isRank: checked } : null
					localStorage.setItem("selMetrics", JSON.stringify(upd)); return upd
				})
			}
		} else if (["MBP", "spotlight", "subregionBoundaries", "adjacency", "catchments", "poimarker"].includes(name)) {
			toggleSwitch({ target: { name, checked } }, name, checked)
			if (name === "subregionBoundaries" && !checked) setSelAggLayer(hexGridOption.value)
		} else {
			if (isChoropleth) setDynamicToggles((prev) => ({ ...prev, [name]: checked }))
			else setDynamicPoiToggles((prev) => ({ ...prev, [name]: checked }))
		}
	}

	// ── Choropleth mode switch ────────────────────────────────────────────────
	const handleChoroplethModeChange = (mode: string, firstCategoricalId?: string) => {
		if (mode === choroplethMode) return
		if (mode === "categorical") {
			preCategoricalCifTypeRef.current = cifType
			preCategoricalPrevRef.current = prev
			preCategoricalDynamicTogglesRef.current = { ...dynamicToggles }
			setCifType([])
			setPrev([])
			setDynamicToggles((prev) => Object.fromEntries(Object.entries(prev).map(([k]) => [k, false])))
			if (firstCategoricalId) {
				setSelectedCategoricalLayer(firstCategoricalId)
				setDynamicToggles((prev) => ({ ...prev, [firstCategoricalId]: true }))
			}
			const currentIsHex = selAggLayer?.key === "hexgrid"
			const currentIsRegion = selAggLayer && selAggLayer.key !== "hexgrid"
			if (currentIsHex && !isHexAvailable) {
				if (isRegionAvailable && selSubRegion) {
					setSelAggLayer(selSubRegion)
					localStorage.setItem("agg-layer", JSON.stringify(selSubRegion))
				} else {
					setSelAggLayer(null)
					localStorage.removeItem("agg-layer")
				}
			} else if (currentIsRegion && !isRegionAvailable) {
				if (isHexAvailable) {
					setSelAggLayer(hexGridOption.value)
					localStorage.setItem("agg-layer", JSON.stringify(hexGridOption.value))
				} else {
					setSelAggLayer(null)
					localStorage.removeItem("agg-layer")
				}
			} else if (!selAggLayer) {
				if (isHexAvailable) {
					setSelAggLayer(hexGridOption.value)
					localStorage.setItem("agg-layer", JSON.stringify(hexGridOption.value))
				} else if (isRegionAvailable && selSubRegion) {
					setSelAggLayer(selSubRegion)
					localStorage.setItem("agg-layer", JSON.stringify(selSubRegion))
				}
			}
		} else {
			if (preCategoricalCifTypeRef.current.length) {
				setCifType(preCategoricalCifTypeRef.current)
				setPrev(preCategoricalPrevRef.current)
				preCategoricalCifTypeRef.current = []
				preCategoricalPrevRef.current = []
			}
			const savedToggles = preCategoricalDynamicTogglesRef.current
			preCategoricalDynamicTogglesRef.current = {}
			setDynamicToggles((prev) => ({
				...prev,
				...(selectedCategoricalLayer ? { [selectedCategoricalLayer]: false } : {}),
				...savedToggles,
			}))
		}
		setChoroplethMode(mode as "numeric" | "categorical")
		if (mode === "categorical") {
			topRightToast.success(
				"Categorical Choropleth Mode Activated",
				"Numerical layers are now disabled since they cannot be viewed in Categorical mode."
			)
		} else {
			topRightToast.success(
				"Numerical Choropleth Mode Activated",
				"Categorical layers are now disabled since they cannot be viewed in Numerical mode."
			)
		}
	}

	const handleCategoricalSelect = (layerId: string) => {
		if (selectedCategoricalLayer && selectedCategoricalLayer !== layerId)
			setDynamicToggles((prev) => ({ ...prev, [selectedCategoricalLayer]: false }))
		setSelectedCategoricalLayer(layerId)
		if (layerId) {
			setDynamicToggles((prev) => ({ ...prev, [layerId]: true }))
		}
	}

	// ── Metric popup ──────────────────────────────────────────────────────────
	const handleOpenPopup = (type: any) => {
		setIsOpenMetricPopup((prev) => ({
			EH: false, CTV: false, PoI: false, catchments: false, subregionBoundaries: false,
			[type]: !prev[type as keyof typeof prev],
		}))
	}

	// ── Category / brand list popups ──────────────────────────────────────────
	const handleListOpen = async (keyName: string, tagName?: string, layerKey?: string, buttonEl?: HTMLElement | null) => {
		// if (tagName === "svg") return
		if (keyName === "categories") {
			const shouldClose = isListOpen.categories === layerKey
			setIsListOpen({ categories: shouldClose ? false : (layerKey ?? false), brands: false, aggLayer: false })
			if (refs.button.current) {
				const r = refs.button.current.getBoundingClientRect()
				setTimeout(() => {
					if (expandedPresenceConfig != null) {
						setPopupPos({ top: "auto", bottom: window.innerHeight - r.bottom - 10, left: r.right + 20 })
					} else {
						const pr = refs.catCheckBox.current?.getBoundingClientRect()
						setPopupPos(pr ? { bottom: 0, left: r.right + 20, top: "auto" } : { bottom: 70, left: r.right + 20, top: "auto" })
					}
				}, 0)
			}
		} else {
			const shouldClose = isListOpen.brands === layerKey
			setIsListOpen({ categories: false, brands: shouldClose ? false : (layerKey ?? false), aggLayer: false })
			const btnEl = buttonEl || refs.brandButton.current
			if (btnEl) {
				const r = btnEl.getBoundingClientRect()
				setTimeout(() => {
					if (expandedPresenceConfig != null) {
						setBrandPopupPos({ top: "auto", bottom: window.innerHeight - r.bottom - 10, left: r.right + 20 })
					} else {
						const pr = refs.brandCheckBox.current?.getBoundingClientRect()
						setBrandPopupPos(pr ? { bottom: "auto", left: r.right + 20, top: r.top - 180 } : { bottom: 70, left: r.right + 20, top: "auto" })
					}
				}, 0)
			}
		}
	}

	const handleClickOutside = (event: any) => {
		const isOutside = Object.values(overlayRefs).some(
			(ref) => ref.current && !ref.current.contains(event.target as Node)
		)
		if (isOutside)
			setIsOpenMetricPopup({ EH: false, CTV: false, PoI: false, catchments: false, subregionBoundaries: false })
		setIsListOpen((prev) => {
			const u = { ...prev }
			if (refs.catCheckBox.current && !refs.catCheckBox.current.contains(event.target)) u.categories = false
			if (refs.brandCheckBox.current && !refs.brandCheckBox.current.contains(event.target)) u.brands = false
			if (refs.aggLayerPopup.current && !refs.aggLayerPopup.current.contains(event.target as Node)) u.aggLayer = false
			return u
		})
	}

	const handleAggLayerChange = (layer: any) => {
		setSelAggLayer(layer)
		setIsListOpen({ categories: false, brands: false, aggLayer: false })
		localStorage.setItem("agg-layer", JSON.stringify(layer))
	}

	const getPayload = (geoId: GeoId) => ({ geoId, ...selCategoryList })

	// ── Search filter ─────────────────────────────────────────────────────────
	const matchesSearch = (layer: any) =>
		!searchQuery.trim() || layer.title?.toLowerCase().includes(searchQuery.toLowerCase())

	// ── OverlayMetricPopup helpers ────────────────────────────────────────────
	const getMetricsForLayer = (layer: any) => {
		if (layer.type === "subregionBoundaries") {
			if (catchment) return null
			return BOUNDARY_TYPES[getCurrGeoLevelName(selectedGeo) as keyof typeof BOUNDARY_TYPES]
		}
		if (layer.type === "catchments") {
			if (pathname === RouteConstants.catchments) return null
			return catchmentTypes
		}
		return layer.options
	}
	const getSelectedMetric = (layer: any) => {
		if (layer.type === "subregionBoundaries") return selSubRegion
		if (layer.type === "catchments") return selCatchmentType
		return selMetric[layer.type as MetricKey]
	}
	const getMetricSetter = (layer: any) => {
		if (layer.type === "subregionBoundaries") return setSelSubRegion
		if (layer.type === "catchments") return setSelCatchmentType
		return setSelMetric
	}

	// ── Effects ───────────────────────────────────────────────────────────────
	useEffect(() => {
		if (!isHexAvailable && selAggLayer?.key === "hexgrid") {
			setSelAggLayer(null); localStorage.removeItem("agg-layer")
		}
	}, [isHexAvailable])

	useEffect(() => {
		const saved = localStorage.getItem("agg-layer")
		if (!saved) return
		try {
			const parsed = JSON.parse(saved)
			if (parsed?.key === "hexgrid" && !selectedGeo.district) return
			setSelAggLayer(parsed)
		} catch { }
	}, [])

	useEffect(() => {
		const saved = localStorage.getItem("agg-layer")
		const isHexSel = saved && JSON.parse(saved)?.key === "hexgrid"
		const isHexAvail = selectedGeo.district || selectedGeo.village
		if (selSubRegion) {
			if (isChecked?.subregionBoundaries) {
				if (!isHexSel || !isHexAvail) { setSelAggLayer(selSubRegion); localStorage.setItem("agg-layer", JSON.stringify(selSubRegion)) }
			} else {
				if (isHexAvail || pathname === RouteConstants.catchments) { setSelAggLayer(hexGridOption.value); localStorage.setItem("agg-layer", JSON.stringify(hexGridOption.value)) }
			}
		} else if (selectedGeo.village || !isChecked?.subregionBoundaries) {
			setSelAggLayer(hexGridOption.value); localStorage.setItem("agg-layer", JSON.stringify(hexGridOption.value))
		}
	}, [selSubRegion, selectedGeo?.village, selectedGeo?.district, catchmentId, isChecked?.subregionBoundaries, pathname])

	useEffect(() => {
		const saved = localStorage.getItem("selCategoryList")
		if (saved) setSelCategoryList(JSON.parse(saved))
	}, [])

	useEffect(() => {
		if (geoCode) cifService.getMarkerFilters(geoCode)
	}, [geoCode])

	useEffect(() => {
		if (poiCategories?.length > 0) {
			if (selCategoryList?.category?.length === 0) {
				const cats = {
					category: poiCategories.map((c: any) => c.key) || [],
					subcategory: poiCategories.flatMap((c: any) => c.sub_categories?.map((s: any) => s.key) || [])
						.filter((k: any): k is number => k !== undefined),
				}
				setSelCategoryList(cats); localStorage.setItem("selCategoryList", JSON.stringify(cats))
			}
			setSelectedCategory(poiCategories[0])
		}
	}, [poiCategories, selCategoryList?.category])

	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside)
		return () => document.removeEventListener("mousedown", handleClickOutside)
	}, [])

	useEffect(() => { dynamicPoiTogglesRef.current = dynamicPoiToggles }, [dynamicPoiToggles])

	useEffect(() => {
		setToggleState({
			MBP: isChecked.MBP, spotlight: isChecked.spotlight, poimarker: isChecked.poimarker,
			adjacency: isChecked.adjacency,
			EH: catchment ? cifType.includes("EH") && (isChecked.subregionBoundaries || isChecked.catchments) : cifType.includes("EH"),
			CTV: cifType.includes("CTV"), PoI: cifType.includes("PoI"),
			subregionBoundaries: isChecked.catchments && !catchment ? false : isChecked.subregionBoundaries,
			catchments: isChecked.catchments,
			...dynamicToggles, ...dynamicPoiToggles,
		})
	}, [isChecked, cifType, dynamicToggles, dynamicPoiToggles])

	useEffect(() => {
		if (isChecked?.catchments && totalOverlayCount >= 1)
			setError({ type: "MapInfo", message: "Catchment view does not support filters. Any previously applied filters are not considered here." })
	}, [isChecked?.catchments])

	useEffect(() => {
		if (!poiColors?.legendProperties) return
		const legend = poiColors.legendProperties[subLevel]
		if (!legend?.isDataAvailable) return
		const available: string[] = Array.isArray(legend.isDataAvailable) ? (legend?.isDataAvailable ?? []) : []
		const off: Record<string, boolean> = {}
		numericChoroplethItems.forEach((layer: any) => {
			const k = layer.layer_id || layer.type
			const id = layer.name || layer.type || k
			const isOn = dynamicToggles[k] !== undefined ? dynamicToggles[k] : !!toggleState[k]
			if (isOn && !available.includes(k) && !available.includes(id)) off[k] = false
		})
		if (Object.keys(off).length > 0) setDynamicToggles((prev) => ({ ...prev, ...off }))
	}, [poiColors, subLevel, layersData])

	useEffect(() => {
		const enabled = numericChoroplethItems.filter((l: any) => {
			const k = getToggleKey(l)
			return dynamicToggles[k] !== undefined ? dynamicToggles[k] : !!toggleState[k]
		}).length
		if (enabled === 5 && prevChoroplethCountRef.current === 4)
			topRightToast.warning("You have reached the limit of 5 active choropleth layers.")
		prevChoroplethCountRef.current = enabled
	}, [dynamicToggles, toggleState, layersData])

	useEffect(() => {
		const poiItems = allMarkerItems.filter((l: any) => l.markertype?.toLowerCase() === "poi")
		const enabled = poiItems.filter((l: any) => {
			const k = getToggleKey(l)
			return dynamicPoiToggles[k] !== undefined ? dynamicPoiToggles[k] : !!toggleState[k]
		}).length
		if (enabled === 5 && prevPoiMarkerCountRef.current === 4)
			topRightToast.warning("You have reached the limit of 5 active POI marker layers.")
		prevPoiMarkerCountRef.current = enabled
	}, [dynamicPoiToggles, toggleState, layersData])

	useEffect(() => {
		const activeDynamic = Object.entries(dynamicToggles).filter(([, v]) => v).map(([k]) => k)
		if (prev.length === 0 && activeDynamic.length === 0) return
		let type = [...new Set(prev)]
		if (selectedGeo.village && !catchment) {
			type = type.filter((i) => i !== "CTV")
			if (prev.includes("PoI") && !type.includes("PoI")) type.push("PoI")
		} else if (selectedGeo.district && !catchment) {
			type = type.filter((i) => i !== "CTV")
		}
		if (poiColors?.legendProperties) {
			const legend = poiColors.legendProperties[subLevel]
			if (legend?.isDataAvailable) {
				const avail: string[] = Array.isArray(legend.isDataAvailable) ? (legend?.isDataAvailable ?? []) : []
				type = type.filter((i) => avail.includes(i))
			}
		}
		type = [...new Set([...type, ...activeDynamic])]
		setCifType(type)
		const updated = JSON.parse(localStorage.getItem("selMetrics")!) || {}
		setSelMetric(() => {
			Object.entries(updated).forEach(([key]) => {
				updated[key as CIFType] = { ...updated[key as CIFType], isRank: type.includes(key as CIFType) }
			})
			localStorage.setItem("selMetrics", JSON.stringify(updated)); return updated
		})
	}, [poiColors, brands, dynamicToggles])

	// Reset per-geo state when geo changes
	useEffect(() => {
		setSelMarkerCategories({})
	}, [geoCode]) // eslint-disable-line react-hooks/exhaustive-deps

	// ── Brands TanStack Query ─────────────────────────────────────────────────
	const brandsPayload = React.useMemo(() => {
		const shouldFetch =
			pathname === RouteConstants.root && cifType &&
			(geoCode === selectedGeo.sub_district || String(geoCode) === String(selectedGeo.village)) &&
			(selCategoryList.category.length > 0 || selCategoryList.subcategory.length > 0)
		if (!shouldFetch) return null
		const { parent } = getParentGeoId(geoCode, selectedGeo, selectedTab)
		const allPoiTrue = Object.keys(dynamicPoiToggles).reduce((acc, k) => { acc[k] = true; return acc }, {} as Record<string, boolean>)
		const base = {
			...getPayload(geoCode), parentId: parent,
			adjacency: isChecked?.adjacency || false,
			filters: filters.map(cleanFilterTree),
			marker_filters: markerFiltersPayload.map(cleanFilterTree),
			catchment: isChecked?.catchments ? [selCatchmentType?.title] : [],
			hex: selectedGeo?.district ? selAggLayer?.key === "hexgrid" || selAggLayer == null : false,
			custom_choropleth_ids: { ...dynamicToggles },
			n_layer_category: Object.entries(selMarkerCategories)
				.filter(([key]) => key !== 'poimarker' && key !== 'PoI')
				.flatMap(([layerKey, sel]) => {
					const rawCats = markerFilterData[layerKey]?.categories ?? []
					const cats = rawCats.map((c: any, i: number) => c.key !== undefined ? c : { ...c, key: i })
					const selectedKeys = new Set(sel.category)
					return cats
						.filter((c: any) => selectedKeys.has(c.key))
						.map((c: any) => ({ key: c.key, name: c.name, layer_id:c.layer_id }))
				}),
		}
		baseBrandsPayloadRef.current = base
		return { ...base, ...allPoiTrue }
	}, [pathname, cifType, geoCode, selectedGeo, selectedTab, selCategoryList, filters, markerFiltersPayload, isChecked?.adjacency, isChecked?.catchments, selCatchmentType, catchment, selAggLayer?.key, dynamicToggles, selMarkerCategories, markerFilterData])

	const { data: brandsData, isLoading: isBrandsLoading } = useQuery({
		queryKey: ["brands", brandsPayload],
		queryFn: async () => { if (!brandsPayload) return null; return await mapService.getBrands(brandsPayload) },
		enabled: !!brandsPayload, staleTime: 5 * 60 * 1000,
	})

	useEffect(() => {
		if (!brandsData) return
		setBrands((prev) => ({ ...prev, ...brandsData }))
		const prevType = prevBrandsTypeRef.current
		const { type: currentType } = getParentGeoId(geoCode, selectedGeo, selectedTab)
		let shouldPreserve: boolean
		if (!prevType) {
			shouldPreserve = true
		} else if ((prevType === "pincode" && currentType === "village") || (prevType === "village" && currentType === "pincode")) {
			shouldPreserve = prevGeoRef.current?.district === selectedGeo?.district
		} else {
			shouldPreserve = isSameHierarchy(prevGeoRef.current, selectedGeo)
		}
		if (!shouldPreserve) setSelBrands([])
		prevGeoRef.current = selectedGeo
		prevBrandsTypeRef.current = currentType
	}, [brandsData])

	useEffect(() => {
		if (!isBrandsLoading) return
		setMapSpinner((p) => p + 1)
		return () => setMapSpinner((p) => p - 1)
	}, [isBrandsLoading])

	// ── Single layer row renderer ─────────────────────────────────────────────
	const renderLayerRow = (layer: any, isChoropleth: boolean) => {
		const toggleKey = getToggleKey(layer)
		const isToggled = isLayerToggled(layer)
		const handleLayerListOpen = (keyName: string, tagName?: string, buttonEl?: HTMLElement | null) =>
			handleListOpen(keyName, tagName, String(toggleKey), buttonEl)
		const layerIsListOpen = {
			categories: isListOpen.categories === String(toggleKey),
			brands: isListOpen.brands === String(toggleKey),
			aggLayer: isListOpen.aggLayer,
		}
		const disabled = isLayerDisabled(layer, isChoropleth)
		const hasMetricPopup =
			(["EH", "CTV", "PoI"].includes(layer.type) ||
				layer.type === "catchments" ||
				(layer.type === "subregionBoundaries" && (!selectedGeo.village || catchment))) &&
			isToggled
		const infoText = hasMetricPopup
			? getLayerInfoText(layer.type, disabled, isToggled, isChecked, geoCode, selectedGeo, selCatchmentType, selMetric, selSubRegion, catchment) || null
			: null

		const showColorDot = layer.type !== "spotlight" && layer.type !== "MBP" && layer.type !== "PoI" && layer.type !== "EH" && layer.type !== "CTV" && (layer?.markertype || layer.type === "poimarker")
		const colorDot = showColorDot ? (() => {
			const [r, g, b] = getMarkerColor(layer.type || "poimarker")
			return (
				<span
					className="shrink-0 rounded-full inline-block h-2 w-2"
					style={{
						backgroundColor: `rgb(${Math.round(r * 0.35 + 166)},${Math.round(g * 0.35 + 166)},${Math.round(b * 0.35 + 166)})`,
						border: `1px solid rgb(${Math.round(r * 0.3)},${Math.round(g * 0.3)},${Math.round(b * 0.3)})`,
					}}
				/>
			)
		})() : undefined

		const isGroupActive = layerIsListOpen.categories || layerIsListOpen.brands

		return (
			<React.Fragment key={toggleKey}>
				<div className={`flex flex-col px-2 mx-2 py-2.5 gap-1 rounded-[var(--semantic-rounded-sm)] ${isGroupActive ? 'bg-[var(--ds-alpha-neutral-3)]' : ''}`}>
					<LayerCheckboxRow
						layer={layer}
						isChecked={isToggled}
						disabled={layer?.markertype?.toLowerCase() === "poi" ? false : disabled}
						onCheckedChange={(checked) => handleToggleChange(checked, toggleKey, layer.options, isChoropleth)}
						customTooltip={getCustomTooltip(layer, isChoropleth)}
						leftDecoration={colorDot}
					/>

					{hasMetricPopup && (
						<OverlayMetricPopup
							type={layer.type}
							isChecked={isToggled}
							isDisabled={disabled}
							isOpen={isOpenMetricPopup[layer.type as keyof typeof isOpenMetricPopup] || false}
							handleOpen={() => handleOpenPopup(layer.type)}
							metrics={getMetricsForLayer(layer)}
							selMetric={getSelectedMetric(layer)}
							setSelMetric={getMetricSetter(layer)}
							ref={overlayRefs[layer.type as keyof typeof overlayRefs] || undefined}
							infoText={infoText}
						/>
					)}

					{(layer?.markertype === "poi" && isToggled) && (
						<POICategoryListHome
							isOpen={layerIsListOpen.categories}
							handleListOpen={handleLayerListOpen}
							onCloseFilterPopup={() => setIsListOpen({ categories: false, brands: false, aggLayer: false })}
							catCheckBoxRef={refs.catCheckBox}
							buttonRef={refs.button}
							isListOpen={layerIsListOpen}
							catchment={catchment}
							isAggLayerOff={isAggLayerOff}
							layer={layer}
						/>
					)}

					{layer?.markertype === "poi" && isToggled && (
						<BrandWatchList
							isOpen={layerIsListOpen.brands}
							handleListOpen={handleLayerListOpen}
							brandCheckBoxRef={refs.brandCheckBox}
							brandButtonRef={refs.brandButton}
							style={{ left: brandPopupPos.left, bottom: 0 }}
							layer={layer}
						/>
					)}

					{((layer?.type === "PoI" && isToggled) || (layer?.markertype && layer.markertype !== "poi" && layer.type !== "spotlight" && layer.type !== "MBP" && isToggled)) && (
						<POICategoryListHome
							isOpen={layerIsListOpen.categories}
							handleListOpen={handleLayerListOpen}
							onCloseFilterPopup={() => setIsListOpen({ categories: false, brands: false, aggLayer: false })}
							catCheckBoxRef={refs.catCheckBox}
							buttonRef={refs.button}
							isListOpen={layerIsListOpen}
							catchment={catchment}
							isAggLayerOff={isAggLayerOff}
							layer={layer}
						/>
					)}

					{layer?.markertype && layer.markertype !== "poi" && layer.type !== "spotlight" && layer.type !== "MBP" && isToggled && brands?.[layer.type]?.[0]?.companyName && (
						<BrandWatchList
							isOpen={layerIsListOpen.brands}
							handleListOpen={handleLayerListOpen}
							brandCheckBoxRef={refs.brandCheckBox}
							brandButtonRef={refs.brandButton}
							style={{ left: brandPopupPos.left, bottom: 0 }}
							layer={layer}
						/>
					)}

					{layer.type === "MBP" && isToggled && (
						<PresenceConfig
							isChecked={isChecked}
							toggleSwitch={toggleSwitch}
							expandedTopDistricts={expandedTopDistricts}
							setExpandedTopDistricts={setExpandedTopDistricts}
							fetchMyPresence={fetchMyPresence}
							setMbpFilter={setMbpFilter}
							presenceTypeCount={presenceTypeCount}
							mapSpinner={mapSpinner}
							myPresence={myPresence}
							myBusinessPresenceConfig={myBusinessPresenceConfig}
							setMyBusinessPresenceConfig={setMyBusinessPresenceConfig}
						/>
					)}

					{layer.type === "PoI" && isPoiOverlapped && (
						<Typography variant="b3" className="italic text-muted-foreground">
							* PoIs are only partially covered for this{" "}
							{catchment ? "Catchment" : selectedGeo?.village ? "Village" : "PIN Code"}
						</Typography>
					)}
				</div>
			</React.Fragment>
		)
	}

	// ── Section content renderers (one per mapping type) ──────────────────────

	/** Borders / generic — standard checkbox rows with visibility filters */
	const renderBordersContent = (items: any[]) => {
		const filtered = items.filter(
			(l: any) =>
				!(l.type === "subregionBoundaries" && selectedGeo.village && !catchment) &&
				!(l.type === "adjacency" && geoCode === selectedGeo.country && pathname === RouteConstants.root) &&
				!(l.type === "catchments" && String(geoCode) === String(selectedGeo.village)) &&
				matchesSearch(l)
		)
		if (!filtered.length) return null
		return (
			<>
				{filtered.map((l: any) => (
					<React.Fragment key={getToggleKey(l)}>
						{renderLayerRow(l, false)}
						{l.type === "subregionBoundaries" && !!isChecked?.catchments && (
							<div className="-mt-2.5 px-4 pb-1.5">
								<Typography variant="b3" className="text-muted-foreground text-[0.625rem]">
									Disabled due to Catchments
								</Typography>
							</div>
						)}
					</React.Fragment>
				))}
			</>
		)
	}

	/** Markers — standard checkbox rows with POI visibility filter */
	const renderMarkersContent = (items: any[]) => {
		const filtered = items.filter(
			(l: any) =>
				!(l.markertype?.toLowerCase() === "poi" && !(catchment || selectedGeo.sub_district || selectedGeo.village)) &&
				matchesSearch(l)
		)
		if (!filtered.length) return null
		return <>{filtered.map((l: any) => renderLayerRow(l, false))}</>
	}

	/** Choropleth — splits items into numeric / categorical sub-groups */
	const renderChoroplethContent = (items: any[], description?: string | null) => {
		const legend = poiColors?.legendProperties?.[subLevel]
		const hasData: string[] = Array.isArray(legend?.isDataAvailable) ? legend!.isDataAvailable : []
		const hasDataAvailabilityInfo = Array.isArray(legend?.isDataAvailable)

		const numericItems = items.filter((l: any) => l["sub-mapping"] === "numeric")
		const categoricalItems = items.filter((l: any) =>
			l["sub-mapping"] == "categorical" ||
			(l?.status === "inprogress" && l["sub-mapping"] !== "numeric")
		)
		const filteredNumeric = numericItems
			.filter(matchesSearch)
			.filter((l: any) => {
				if (!hasDataAvailabilityInfo) return true
				if (l?.status === "inprogress") return true
				const k = getToggleKey(l)
				const id = l.name || l.type || k
				return hasData.includes(k) || hasData.includes(id)
			})
		const filteredCategorical = categoricalItems
			.filter(matchesSearch)
			.filter((l: any) => {
				if (!hasDataAvailabilityInfo) return true
				if (l?.status === "inprogress") return true
				const k = getToggleKey(l)
				const id = l.name || l.type || k
				return hasData.includes(k) || hasData.includes(id)
			})
		if (!filteredNumeric.length && !filteredCategorical.length) return null
		return (
			<>
				{/* Notices */}
				{description && (
					<div className="px-4 pt-3 pb-2">
						<Typography
							variant="b3"
							className="text-[var(--ds-solid-neutral-11)] text-[0.625rem] leading-[1.2] font-normal"
							dangerouslySetInnerHTML={{ __html: description }}
						/>
					</div>
				)}

				{/* Mode selector */}
				<div className="px-4 pb-3">
					<RadioGroup
						value={choroplethMode}
						className="gap-2"
					>

						{/* ── Numerical Mode ── */}
						<div
							className={`flex flex-col border border-border rounded-md overflow-hidden ${choroplethMode === "numeric" ? "border-l-[3px] border-l-[var(--ds-solid-neutral-10)]" : ""} ${choroplethMode === "categorical" ? "opacity-50" : ""}`}
							onClick={() => handleChoroplethModeChange("numeric", filteredCategorical[0]?.layer_id || filteredCategorical[0]?.type)}
						>
							<RadioCard
								value="numeric"
								label="Numerical Mode"
								icon={
									<span className="material-symbols-outlined leading-none" style={{ fontSize: 20 }}>
										123
									</span>
								}
								className="rounded-none border-0 border-b border-border"
							/>
							{filteredNumeric.length > 0 && (
								<div className={`flex flex-col bg-muted/40 ${choroplethMode === "categorical" ? "pointer-events-none" : ""}`}>
									{filteredNumeric.map((l: any) => renderLayerRow(l, true))}
								</div>
							)}
						</div>

						{/* ── Categorical Mode ── */}
						<TooltipProvider>
							<TooltipRoot open={isAggLayerOff ? undefined : false}>
								<TooltipTrigger asChild>
									<div
										className={`flex flex-col border border-border rounded-md overflow-hidden ${choroplethMode === "categorical" ? "border-l-[3px] border-l-[var(--ds-solid-neutral-10)]" : ""} ${choroplethMode === "numeric" ? "opacity-50" : ""} ${isAggLayerOff ? "opacity-50 cursor-not-allowed" : ""}`}
										onClick={() => {
											if (isAggLayerOff) return
											handleChoroplethModeChange("categorical", filteredCategorical[0]?.layer_id || filteredCategorical[0]?.type)
										}}
									>
								<div className={isAggLayerOff ? "pointer-events-none" : ""}>
								<RadioCard
									value="categorical"
									label="Categorical Mode"
									icon={
										<span className="material-symbols-outlined leading-none" style={{ fontSize: 20 }}>
											category
										</span>
									}
									className="rounded-none border-0 border-b border-border"
								/>
								{(
									<div className={`px-3 py-2 bg-muted/40 ${choroplethMode === "numeric" || legendShimmer ? "pointer-events-none" : ""}`}>
										{filteredCategorical.length > 0 ? (
											<>
												<Select
													size="small"
													className="text-xs"
													placeholder="Select a layer"
													value={selectedCategoricalLayer}
													onValueChange={handleCategoricalSelect}
													startAdornment={filteredCategorical.find((l: any) => (l.layer_id || l.type) === selectedCategoricalLayer)?.status === "inprogress" ? (
														<svg className="w-[0.75em] h-[0.75em] shrink-0 animate-spin text-muted-foreground" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
															<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
															<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
														</svg>
													) : undefined}
													options={filteredCategorical.map((l: any) => ({
														value: l.layer_id || l.type,
														label: l.title,
														disabled: l.status === "inprogress" || !!legendShimmer,
														prefix: l.status === "inprogress" ? (
															<svg
																className="w-[0.75em] h-[0.75em] shrink-0 animate-spin"
																viewBox="0 0 24 24"
																fill="none"
																xmlns="http://www.w3.org/2000/svg"
															>
																<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
																<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
															</svg>
														) : undefined,
													}))}
												/>
												
											</>
										) : (
											<div className="flex flex-col gap-2 py-1">
												<Typography variant="b3" className="text-muted-foreground leading-[1.4]">
													No categorical layers available at this level. Add a nominal or ordinal categorical layer to view it on map.
												</Typography>
												<Button
													variant="secondary"
													// size="sm"
													className="w-full"
													onClick={() => navigate(`${RouteConstants.layers}?browse=true`)}
												>
													Add Layer
												</Button>
											</div>
										)}
									</div>
								)}
									</div>
									</div>
								</TooltipTrigger>
								<TooltipContent side="top">
									Turn on Choropleth to enable this toggle
								</TooltipContent>
							</TooltipRoot>
						</TooltipProvider>
					</RadioGroup>
				</div>
			</>
		)
	}

	/**
	 * Master dispatcher — given a mapping group from layersData,
	 * returns the section's inner content (or null if nothing to show).
	 */
	const renderSectionContent = (group: any): JSX.Element | null => {
		const { mapping, items = [] } = group
		switch (mapping) {
			case "borders":
			case "generic":
				return renderBordersContent(items)
			case "markers":
				return renderMarkersContent(items)
			case "choropleth":
				return renderChoroplethContent(items, group.description)
			default:
				return null
		}
	}

	// ── Render ────────────────────────────────────────────────────────────────
	return (
		<div className="left-sidebar relative h-full flex flex-col bg-white">

			{/* Scrollable content + overlaid Filter Regions button */}
			<div className="relative flex-1 min-h-0">
				<div className="h-full overflow-y-auto overflow-x-hidden no-scrollbar flex flex-col">

					{/* Search */}
					<div className="px-3 py-4 bg-epic-neutral-2 sticky top-0 z-10">
						<Input
							size="small"
							placeholder="Search layers"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							leftIcon={<Search size={14} />}
							rightIcon={searchQuery ? <Close size={14} onClick={() => setSearchQuery("")} /> : undefined}
							className=""
						/>
					</div>

					{/*
          Data-driven sections.
          Skip mapping="layers" — that feeds <TopDistricts> below the fold.
        */}
					{(() => {
							const sections = layersData
								.filter((group) => group.mapping !== "layers")
								.map((group) => {
									const content = renderSectionContent(group)
									if (!content) return null
									return (
										<CollapsibleSection
											key={group.mapping}
											title={group.title ?? group.mapping}
											icon={getSectionIcon(group.icon)}
											defaultOpen
										>
											{content}
										</CollapsibleSection>
									)
								})
							const hasResults = sections.some(Boolean)
							if (!hasResults && searchQuery.trim()) {
								return (
									<div className="flex flex-1 items-start justify-center py-2">
										<Typography variant="b3" className="text-center text-muted-foreground tracking-[0]">No results found</Typography>
									</div>
								)
							}
							const matchingCount = searchQuery.trim()
								? layersData
									.filter(g => g.mapping !== "layers")
									.flatMap(g => (g.items || []).filter(matchesSearch))
									.length
								: 0
							return (
								<>
									{searchQuery.trim() && (
										<div className="flex items-center gap-[var(--semantic-2xs)] p-[var(--semantic-s)] self-stretch">
											<Typography variant="b3" className="text-[var(--general-text-secondary)] font-normal tracking-[0]">
												Showing all matching results ({matchingCount})
											</Typography>
										</div>
									)}
									{sections}
								</>
							)
						})()}

					{/* Spacer so last item clears the overlaid button */}
					{(pathname === RouteConstants.root || pathname === RouteConstants.catchments) && !isChecked?.catchments && selAggLayer?.key !== 'hexgrid' && (
						<div className="shrink-0 h-14" />
					)}
				</div>

				{/* Filter Regions button — absolute overlay at bottom of scroll area */}
				{(pathname === RouteConstants.root || pathname === RouteConstants.catchments) && !isChecked?.catchments && selAggLayer?.key !== 'hexgrid' && !selectedGeo?.village &&(
					<div className="absolute bottom-0 left-0 right-0 px-4 py-3 flex items-center justify-center backdrop-blur-[1px] bg-[linear-gradient(0deg,rgba(255,255,255,0.20)_5.21%,rgba(255,255,255,0.00)_100%)] pointer-events-none">
						<div className="pointer-events-auto">
							<Button
								variant="primary"
								size="regular"
								rounded="full"
								className="w-auto"
								onClick={() => setShowFilterRegionModal(true)}
							>
								<span className="material-symbols-outlined leading-none" style={{ fontSize: 16 }}>
									filter_alt
								</span>
								<span className="">Filter Regions</span>
								{totalOverlayCount > 0 && (
									<span className="flex items-center justify-center min-w-[1.125rem] h-[1.125rem] rounded-full bg-white px-1 text-[0.625rem] font-medium leading-none text-foreground font-sans">
										{totalOverlayCount}
									</span>
								)}
							</Button>
						</div>
					</div>
				)}
			</div>

			{/* Filter Regions modal */}
			{(pathname === RouteConstants.root || pathname === RouteConstants.catchments) && (
				<FilterRegionModal
					showFilterRegionModal={showFilterRegionModal}
					setShowFilterRegionModal={setShowFilterRegionModal}
					sliders={sliders}
					setSliders={setSliders}
					hideTrigger
				/>
			)}

			{/* Top Districts / Legend */}
			<TopDistricts
				cifType={cifType}
				expandedTopDistricts={expandedTopDistricts}
				setExpandedTopDistricts={setExpandedTopDistricts}
				topfiveregions={Array.isArray(topFiveRegions) ? topFiveRegions : []}
				selCatchmentType={selCatchmentType}
				dataLayersGroup={dataLayersGroup}
				legendShimmer={legendShimmer}
				geoCode={geoCode}
				isPoiOverlapped={isPoiOverlapped}
			/>

			{/* AggLayer popup (triggered from map top-right) */}
			{isListOpen.aggLayer && ((!selectedGeo.village && selSubRegion?.title) || selectedGeo.village) && (
				<AggLayerPopup
					ref={refs.aggLayerPopup}
					options={[
						...(isChecked?.catchments && !catchmentId
							? [catchmentOption]
							: (!selectedGeo.village || (selectedGeo?.village && catchment)) && isChecked?.subregionBoundaries
								? [{ label: selSubRegion.title, value: selSubRegion, key: "subregion" }]
								: []),
						...(((!selectedGeo.village && selSubRegion?.key >= 4) || selectedGeo.village) ? [hexGridOption] : []),
						{ label: "OFF", value: null, key: "off" },
					]}
					selectedKey={
						!selAggLayer ? "off"
							: selAggLayer?.key === "hexgrid" ? "hexgrid"
								: isChecked?.catchments && !catchmentId ? "catchment"
									: "subregion"
					}
					position={aggLayerPopupPos}
					onSelect={handleAggLayerChange}
				/>
			)}

		</div>
	)
}
